// @flow

import type {Reducer} from 'redux';
import type {Axios} from 'axios';
import type {_Action, Action, ActionCreator, ActionEmpty, RootState} from '../types';
import type {Feature, Features, FeaturesState} from './types';

import axios from 'axios';
import {createSelector} from 'reselect';
import {getFeatures} from '../../api/server/features';

type ActionFeaturesSet = _Action<Features>;

export const GAME_KEYS_WIZARD_FEATURE = 'game_keys_package_create';
export const ROLES_FEATURE = 'roles';
export const CD_FEATURE = 'cross-distribution';
export const USER_GROUPS = 'user-group';
export const MULTIPLE_O_S = 'multiple_o_s';
export const CARD_TRACKING_FEATURE = 'integration_guide_notification';
export const CUSTOM_AUTOMATION_FEATURE = 'custom_automation';
export const REFUND_PERMISSION_FEATURE = 'permission_to_refund';
export const SUBS_MULTICURRENCY = 'subs_multicurrency';
export const SUBSCRIPTION_NEW_STORE = 'subs_new_store';
export const SUBSCRIPTIONS_EMAILS_REPORT = 'subs_emails_report';
export const EMAILS_SUPPORT = 'emails_support';
export const SUBSCRIPTION_ANALYTICS = 'subscription_analytics';
export const SITE_BUILDER_ANALYTICS = 'sb_analytics';
export const IGS_ANALYTICS = 'igs_analytics';
export const BB_ANALYTICS = 'bb_analytics';

const rootType = '@publisher/features';
export const FEATURES_SET = `${rootType}/set`;
export const FETCHING_STARTED = `${rootType}/fetching/started`;
export const FETCHING_FINISHED = `${rootType}/fetching/finished`;
export const FEATURE_SET_ENABLED = `${rootType}/set/enabled`;

const initialState: FeaturesState = {
  features: [],
  isFetching: false
};

export default ((state: FeaturesState = initialState, action: Action) => {
  switch (action.type) {
    case FEATURES_SET: {
      return {
        ...state,
        features: action.payload
      };
    }

    case FETCHING_STARTED: {
      return {
        ...state,
        isFetching: true
      };
    }

    case FETCHING_FINISHED: {
      return {
        ...state,
        isFetching: false
      };
    }

    case FEATURE_SET_ENABLED: {
      return {
        ...state,
        features: state.features.map(feature =>
          action.payload.name === feature.name
            ? {
                ...feature,
                enabled: action.payload.enabled
              }
            : feature
        )
      };
    }

    default: {
      return state;
    }
  }
}: Reducer<FeaturesState, Action>);

// Selectors
export const selectFeatureByName = (state: RootState, name: string): Feature | null =>
  state.features.features.find((feature: Feature) => feature.name === name) || null;

export const selectEnabledFeatureByName: (
  RootState,
  string
) => Feature | null = createSelector(selectFeatureByName, (feature): Feature | null =>
  !feature || !feature.enabled ? null : feature
);

export const selectFetching = (state: RootState): boolean => state.features.isFetching;

// Actions
export const setFeatures = (features: Features): ActionFeaturesSet => ({
  type: FEATURES_SET,
  payload: features
});

export const setFeatureEnabled = (name: string, enabled: boolean = false) => ({
  type: FEATURE_SET_ENABLED,
  payload: {name, enabled}
});

export const setFetchingStarted = (): ActionEmpty<typeof FETCHING_STARTED> => ({
  type: FETCHING_STARTED
});

export const setFetchingFinished = (): ActionEmpty<typeof FETCHING_FINISHED> => ({
  type: FETCHING_FINISHED
});

// Action Creators
export const fetchFeatures = (
  merchantId: number,
  axiosInstance: Axios = axios
): ActionCreator => dispatch => {
  dispatch(setFetchingStarted());

  return getFeatures(merchantId, axiosInstance)
    .then(response => {
      dispatch(setFeatures(response.features));
      dispatch(setFetchingFinished());
    })
    .catch(() => dispatch(setFetchingFinished()));
};
